/** @format */

import { API_STATUS } from "core-lib/constants/network";
import { useForm } from "core-lib/hooks";
import { useProfile } from "core-lib/hooks/useProfile";
import { trackCustomGTMEvent } from "core-lib/utils/events";
import { validateAndTrimString } from "core-lib/utils/utils";
import {
  emailValidation,
  phoneNumberValidation,
  requiredFieldValidation,
} from "core-lib/utils/validation";
import { useRouter } from "next/router";
import { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  initState,
  LeadGenerationSource,
  LeadType,
  TestDriveVersion,
} from "testDrive/constants";
import { fetchGenerateLead } from "testDrive/services/fetchGenerateLead";
import { ListType, UserDetailsType } from "testDrive/types";
import { getName } from "testDrive/utils";
import {
  updateDealer,
  updateFormDetails,
  updateLeadVerification,
  updateServiceableOemPopup,
  updateServiceablePopup,
  updateWebSuccess,
} from "../store";
import {
  Attributes,
  TestDriveEvents,
  trackTestDriveEvents,
} from "../utils/events";
import { useCheckServiceable } from "./useCheckServiceable";
import { useUpdateBooking } from "./useUpdateBooking";
import { subscribeCarReview } from "testDrive/services/subscribeCarReview";

interface useFormDetailsProps {
  data?: ListType;
  userDetails: UserDetailsType;
  address: any;
  pageName: string;
  list?: ListType[];
  replace: boolean;
  section: string;
  dealer: any;
  version?: string;
  fireGTMEvent?: boolean;
  leadGenerationSource?: LeadGenerationSource;
}

export const useFormDetails = ({
  data = null,
  userDetails,
  address,
  pageName,
  list,
  replace = false,
  section,
  dealer,
  version = TestDriveVersion.v1,
  fireGTMEvent = true,
  leadGenerationSource = null,
}: useFormDetailsProps) => {
  const { fullName, phoneNumber, email } = useProfile();
  const [generatingLead, setGeneratingLead] = useState(API_STATUS.default);
  const router = useRouter();
  const { updateStateData } = useUpdateBooking();
  const { checkServiceable } = useCheckServiceable();

  const formDetails = useSelector(
    (state: any) => state?.testDrive?.formDetails
  );
  const showServiceablePopup = useSelector(
    (state: any) => state?.testDrive?.showServiceablePopup
  );
  const noRedirect = useSelector((state: any) => state?.testDrive?.noRedirect);
  const dispatch = useDispatch();

  const subscribeForNotification = async (brandId, modelId) => {
    const payload = {
      brand_id: brandId,
      model_id: modelId,
      subscription_level: "model",
      status: "subscribed",
      subscription_type: "reviews",
    };
    await subscribeCarReview(payload);
  };

  const generateLead = async (isDraft = false, isInstantBooking = false) => {
    let isServiceable = true;
    let dealerCar = list?.find((item) => item?.leadType === LeadType.Dealer);
    if (data && !dealerCar && !isInstantBooking && !isDraft) {
      dealerCar = data;
    }
    if (dealerCar) {
      isServiceable = checkServiceable({
        leadType: dealerCar?.leadType,
        city: form?.city?.input,
      });
    }
    if (!isServiceable) {
      dispatch(updateServiceablePopup(true));
      return;
    }
    setGeneratingLead(API_STATUS.in_progress);
    const { firstName, lastName } = getName(form.name.input);

    const ids = [];
    let addDealer = false;
    if (list?.length > 0) {
      list?.forEach((item) => {
        ids.push(item?.id);
        if (item?.metaData?.fetchDealersList && !addDealer) {
          addDealer = true;
        }
      });
    } else {
      ids.push(data?.id);
      if (data?.metaData?.fetchDealersList) {
        addDealer = true;
      }
    }

    const payload: any = {
      first_name: firstName,
      last_name: lastName || firstName,
      email: form.email.input,
      city: form.city.input,
      dealer_name: data?.dealerName,
      model: data?.modelName,
      lead_source: data?.leadSource,
      inventory_ids: ids,
      meta_data: {
        version: version,
      },
      state: address?.data?.state,
      infra_city: address?.data?.infraCity,
      zone_name: address?.data?.zoneName,
      state_id: address?.data?.stateId,
    };
    if (form?.pincode?.input) {
      payload.pincode = `${form.pincode.input}`;
    }
    if (address?.data?.latitude) {
      payload.meta_data = {
        ...payload.meta_data,
        latitude: address?.data?.latitude,
      };
    }
    if (address?.data?.longitude) {
      payload.meta_data = {
        ...payload.meta_data,
        longitude: address?.data?.longitude,
      };
    }
    if (isDraft) {
      payload.status = "DRAFTED";
    }

    if (address?.data?.city) {
      payload.city = address?.data?.city;
    }
    if (address?.data?.pincode) {
      payload.pincode = `${address?.data.pincode}`;
    }

    if (dealer && addDealer) {
      const obj = {
        dealer_id: dealer?.dealerId,
        dealer_trade_name: dealer?.dealerTradeName,
        parkplus_city: dealer?.parkplusCity,
        partner_city: dealer?.partnerCity,
        partner_state: dealer?.partnerSite,
      };
      payload.meta_data = {
        ...payload.meta_data,
        ...obj,
      };
    }
    if (leadGenerationSource) {
      payload.generated_source = leadGenerationSource;
    }
    const response = await fetchGenerateLead(payload);
    if (response) {
      await subscribeForNotification(data?.brandId, data?.modelId);

      let url = `/services/test-drive/confirmation`;
      const query: any = {
        filters: JSON.stringify({
          ids: response,
        }),
      };

      if (!isDraft && response?.length > 0 && !noRedirect) {
        if (!replace || router?.query?.origin) {
          query.origin = router?.query?.origin || "home";
        }
        dispatch(updateLeadVerification({ show: true, leadIds: response }));
      } else {
        if (!isDraft) {
          dispatch(updateLeadVerification({ show: false, leadIds: response }));
          dispatch(updateWebSuccess(true));
        }
      }
      updateStateData({ ids: ids, leadIds: response, isBooked: !isDraft });
      dispatch(updateServiceablePopup(false));
      dispatch(updateServiceableOemPopup(false));
      if (dealer) {
        dispatch(updateDealer(null));
        handleChange("dealer_name", "");
      }
      setGeneratingLead(API_STATUS.success);
      if (!isDraft) {
        trackTestDriveEvents(TestDriveEvents.LeadSubmitted, {
          [Attributes.PageName]: pageName,
          [Attributes.ModelId]:
            data?.modelName ||
            list?.map((item) => item?.displayModelName).join(","),
          [Attributes.Section]: section,
          [Attributes.Leads]: response,
        });
        if (fireGTMEvent) {
          response?.map((item) => {
            trackCustomGTMEvent({
              eventName: "leadCaptured",
              attributes: { eventID: item },
            });
          });
        }
      }
      return response;
    } else {
      setGeneratingLead(API_STATUS.failure);
    }
  };

  const text = useMemo(() => {
    let str = "";
    if (userDetails?.name) {
      str += `${userDetails?.name}, `;
    }
    if (phoneNumber) {
      str += `${phoneNumber}`;
    }
    return str;
  }, [phoneNumber, userDetails]);

  const validateForm = ({ form, name = "all" }) => {
    const formData = { ...form };
    if (name === "all" || name === "name") {
      formData.name.err =
        requiredFieldValidation(formData.name.input).errMsg ||
        validateAndTrimString(formData.name.input).errMsg;
    }
    if (name === "all" || name === "phone") {
      formData.phone.err =
        validateAndTrimString(formData.phone.input).errMsg ||
        phoneNumberValidation(formData.phone.input).errMsg;
    }
    if (name === "all" || name === "city") {
      formData.city.err = validateAndTrimString(formData.city.input).errMsg;
    }
    if (name === "all" || name === "email") {
      if (data?.leadType === LeadType.Oem) {
        formData.email.err = emailValidation(formData.email.input).errMsg;
      }
    }
    if (name === "all" || name === "dealer_name") {
      if (
        data?.metaData?.fetchDealersList ||
        list?.findIndex((item) => item?.metaData?.fetchDealersList) > -1
      ) {
        formData.dealer_name.err = validateAndTrimString(
          formData.dealer_name.input
        ).errMsg;
      }
    }
    const obj: any = {};
    Object.keys(formData)?.map((item) => {
      if (formData[item]?.err === "") {
        obj[item] = formData[item];
      }
    });
    dispatch(updateFormDetails({ ...obj }));
    return formData;
  };

  const handleFormPreFill = () => {
    if (phoneNumber) {
      handleChange("phone", phoneNumber as string);
    }

    if (userDetails?.name) {
      handleChange("name", userDetails?.name);
    } else if (fullName && !form.name.input) {
      handleChange("name", fullName);
    } else if (formDetails?.name?.input && !form.name.input) {
      handleChange("name", formDetails?.name?.input);
    }

    if (userDetails?.pincode) {
      handleChange("pincode", userDetails.pincode);
    } else if (address?.data?.pincode && !form.pincode.input) {
      handleChange("pincode", `${address.data?.pincode}`);
    }

    if (userDetails?.email) {
      handleChange("email", userDetails?.email as string);
    } else if (formDetails?.email?.input && !form?.email?.input) {
      handleChange("email", formDetails?.email?.input);
    } else if (email && !form.email.input) {
      handleChange("email", email as string);
    }

    if (address?.data && !form?.city?.input) {
      handleChange("city", address?.data?.city);
    } else if (
      formDetails?.city?.input &&
      address?.data &&
      !form?.city?.input
    ) {
      handleChange("city", formDetails?.city?.input);
    }
  };

  const { form, handleChange, handleSubmit } = useForm({
    initState: initState,
    callback: generateLead,
    validate: validateForm,
  });

  useEffect(() => {
    handleFormPreFill();
  }, [
    fullName,
    address,
    phoneNumber,
    email,
    userDetails,
    showServiceablePopup,
  ]);

  return {
    generateLead,
    text,
    generatingLead,
    handleChange,
    form,
    handleSubmit,
  };
};
